import {
  GetTransactionsResponse,
  TransactionResponse,
  Payment,
  Refund,
  BookedService,
  Product,
} from "../types/transactions";
import {
  calculateDiscountedServiceTotal,
  calculateDiscountedProductTotal,
} from "./transactionUtils";
import { remainingLinePricing } from "./packageRedemptionUtils";

export interface TaxBreakdownEntry {
  id: number;
  name: string;
  rate: number;
  amount: number;
}

export interface TransactionTotals {
  /** Sum of succeeded payments. */
  paidAmount: number;
  /** Sum of per-service tips. */
  tipsTotal: number;
  undiscountedServiceTotal: number;
  undiscountedProductTotal: number;
  /** undiscountedServiceTotal + undiscountedProductTotal. */
  subtotal: number;
  /** One entry per distinct tax rate, sorted by name. */
  taxBreakdown: TaxBreakdownEntry[];
  salesTax: number;
  /** Discounted services + products (full lines, each floored at 0). */
  discountedLineItemTotal: number;
  /** discountedLineItemTotal + tipsTotal + salesTax (gross, pre-redemption). */
  total: number;
  /**
   * Value settled by package redemptions on this transaction. Redemptions carry
   * no cash and create no payment — they reduce what the client still owes.
   * Kept for the "has redemptions" display condition; the money math below is
   * derived per line from `remainingLinePricing`, not this figure.
   */
  packageCoverage: number;
  /**
   * Subtotal on the remaining-units basis: the un-redeemed fraction of each
   * service line at its full (pre-discount) price, plus products. Redeemed units
   * drop out (covered by the package). Shown in the summary's Subtotal row.
   * Equals `subtotal` when there are no redemptions.
   */
  subtotalAfterRedemptions: number;
  /**
   * Line discount on the remaining-units basis (full − discounted, per line,
   * summed). Because redeemed units are covered by the package, the discount
   * applies only to the units the client pays for. Shown as the Discount row so
   * the column foots: subtotalAfterRedemptions − discountSavings + tax + tip =
   * totalAfterRedemptions.
   */
  discountSavings: number;
  /**
   * Total on the remaining-units basis: discounted remaining line prices +
   * tips + salesTax — what the client owes before payments. Matches the backend
   * net (Σ calc__cost_after_redemptions). Equals `total` with no redemptions.
   */
  totalAfterRedemptions: number;
  /** totalAfterRedemptions − paidAmount. */
  remainingBalance: number;
  isNegativeTotal: boolean;
}

/**
 * Single source of truth for a transaction's money math. Extracted from
 * TransactionSummary so the checkout screen and the summary can never
 * disagree on the remaining balance.
 */
export function computeTransactionTotals(
  transaction: GetTransactionsResponse | TransactionResponse,
): TransactionTotals {
  // Redemptions no longer create payment rows; exclude any legacy
  // package_redemption payments so they never count as cash paid.
  const paidAmount = transaction.payments
    .filter(
      (payment) =>
        payment.status === "succeeded" && payment.type !== "package_redemption",
    )
    .reduce((sum, payment) => sum + payment.amount, 0);

  const tipsTotal = transaction.line_items.booked_services.reduce(
    (total, service) => total + (service.tip || 0),
    0,
  );

  const undiscountedServiceTotal =
    transaction.line_items.booked_services.reduce(
      (sum, service) => sum + service.calc__cost_excl_tips_discounts,
      0,
    );
  const undiscountedProductTotal = transaction.line_items.products.reduce(
    (sum, product) => sum + product.unit_price * product.quantity,
    0,
  );

  // Gift cards sold as line items are tax-exempt and take no discount, tip, or
  // redemption: their face value adds straight to what the client owes (and must
  // be paid before the backend mints the card). Optional array on older/cached
  // responses. Threaded into subtotal + the discounted/after-redemption totals
  // below; it contributes nothing to tax, tips, or discountSavings.
  const giftCardTotal = (transaction.line_items.gift_cards ?? []).reduce(
    (sum, giftCard) => sum + (giftCard.amount || 0),
    0,
  );

  // Package/special sales can now carry a per-line discount (but no tip or
  // redemption). Track two figures: the pre-discount pre-tax base (feeds the
  // Subtotal rows) and the post-discount pre-tax amount (feeds the Total rows).
  // The pre-tax base is the backend `calc__cost_excl_tips_discounts` (NOT raw
  // unit_price × quantity — that would double-count backed-out tax for
  // tax-inclusive omit_tax_collection packages). Post-discount pre-tax is the
  // tax-inclusive charge minus tax (`calc__cost − calc__taxes`); it falls back
  // to the base for older/cached responses without those calc__ fields (treated
  // as no discount). Tax rides in via pushTaxes below, so subtotal + package tax
  // = the amount charged in both tax modes.
  const servicePackages = transaction.line_items.service_packages ?? [];
  const packageMoney = servicePackages.reduce(
    (acc, pkg) => {
      const preTaxBase =
        pkg.calc__cost_excl_tips_discounts ??
        pkg.unit_price * (pkg.quantity ?? 1);
      const postDiscountPreTax =
        pkg.calc__cost != null && pkg.calc__taxes != null
          ? pkg.calc__cost - pkg.calc__taxes
          : preTaxBase;
      acc.preTaxBase += preTaxBase;
      acc.postDiscountPreTax += postDiscountPreTax;
      return acc;
    },
    { preTaxBase: 0, postDiscountPreTax: 0 },
  );
  // Pre-discount base for the Subtotal rows; post-discount for the Total rows.
  const packagePreTaxTotal = packageMoney.preTaxBase;
  const packageDiscountedPreTax = packageMoney.postDiscountPreTax;
  const packageDiscountSavings = packagePreTaxTotal - packageDiscountedPreTax;

  const subtotal =
    undiscountedServiceTotal +
    undiscountedProductTotal +
    packagePreTaxTotal +
    giftCardTotal;

  // Per-rate tax breakdown from the line_items taxes arrays: group by
  // tax_rate_id and render one row per distinct rate so the operator can see
  // exactly which tax contributed how much.
  const grouped = new Map<number, TaxBreakdownEntry>();
  const pushTaxes = (items: Array<{ taxes?: any[] }>) => {
    items.forEach((item) => {
      (item.taxes || []).forEach((tax: any) => {
        const key = tax.tax_rate_id;
        const existing = grouped.get(key);
        if (existing) {
          existing.amount += tax.tax_amount || 0;
        } else {
          grouped.set(key, {
            id: key,
            name: tax.name,
            rate: tax.rate,
            amount: tax.tax_amount || 0,
          });
        }
      });
    });
  };
  pushTaxes(transaction.line_items.booked_services);
  pushTaxes(transaction.line_items.products);
  pushTaxes(servicePackages);
  const taxBreakdown = Array.from(grouped.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  const salesTax = taxBreakdown.reduce((sum, t) => sum + t.amount, 0);

  // Packages contribute their post-discount pre-tax amount to the discounted
  // line-item total (equals the pre-tax base when no discount is applied).
  const discountedLineItemTotal =
    calculateDiscountedServiceTotal(transaction) +
    calculateDiscountedProductTotal(transaction) +
    packageDiscountedPreTax +
    giftCardTotal;
  const total = discountedLineItemTotal + tipsTotal + salesTax;

  // Package redemptions settle part of the bill without a payment. On the
  // remaining-units basis, each service line contributes only the fraction the
  // client still pays for (line quantity − units redeemed): its full
  // (pre-discount) price feeds the Subtotal, its discounted price feeds the
  // Total. Redeemed units are covered by the package and drop out entirely —
  // the discount therefore applies only to the units the client pays for,
  // matching the backend net (Σ calc__cost_after_redemptions).
  const { serviceFullRemaining, serviceDiscountedRemaining } =
    transaction.line_items.booked_services.reduce(
      (acc, service) => {
        const pricing = remainingLinePricing(service);
        acc.serviceFullRemaining += pricing.full;
        acc.serviceDiscountedRemaining += pricing.discounted;
        return acc;
      },
      { serviceFullRemaining: 0, serviceDiscountedRemaining: 0 },
    );
  const discountedProductTotal = calculateDiscountedProductTotal(transaction);

  // packageCoverage stays as the gross value redemptions removed (full line
  // cost − net), purely so the "has redemptions" display condition still fires;
  // the money math above no longer flows through it.
  const packageCoverage = transaction.line_items.booked_services.reduce(
    (sum, service) => {
      const cost = service.calc__cost ?? 0;
      const net = service.calc__cost_after_redemptions ?? cost;
      return sum + Math.max(0, cost - net);
    },
    0,
  );

  // Subtotal (full remaining) − Discount (savings) + tax + tip = Total, so the
  // summary column foots. Discount is the full→discounted delta on the units
  // still billed, for services and products alike.
  // Packages are never redeemed, so they add their pre-discount pre-tax base to
  // the remaining Subtotal and their post-discount pre-tax to the remaining
  // Total (tax is already in salesTax); the delta feeds discountSavings.
  const subtotalAfterRedemptions =
    serviceFullRemaining +
    undiscountedProductTotal +
    packagePreTaxTotal +
    giftCardTotal;
  const discountSavings =
    serviceFullRemaining -
    serviceDiscountedRemaining +
    (undiscountedProductTotal - discountedProductTotal) +
    packageDiscountSavings;
  const totalAfterRedemptions =
    serviceDiscountedRemaining +
    discountedProductTotal +
    tipsTotal +
    salesTax +
    packageDiscountedPreTax +
    giftCardTotal;
  const remainingBalance = totalAfterRedemptions - paidAmount;

  return {
    paidAmount,
    tipsTotal,
    undiscountedServiceTotal,
    undiscountedProductTotal,
    subtotal,
    taxBreakdown,
    salesTax,
    discountedLineItemTotal,
    total,
    packageCoverage,
    subtotalAfterRedemptions,
    discountSavings,
    totalAfterRedemptions,
    remainingBalance,
    isNegativeTotal: total < 0,
  };
}

/** Refunds worth showing in the UI (succeeded or still pending). */
export function visibleRefunds(
  transaction: GetTransactionsResponse | TransactionResponse,
): Refund[] {
  return (transaction.refunds || []).filter(
    (r) => r.status === "succeeded" || r.status === "pending",
  );
}

/** Refunds nested under the payment they reverse. */
export function refundsForPayment(
  transaction: GetTransactionsResponse | TransactionResponse,
  payment: Payment,
): Refund[] {
  return visibleRefunds(transaction).filter((r) => r.payment_id === payment.id);
}

/**
 * Remaining refundable amount on a payment, rounded to cents so a fully
 * refunded payment doesn't leave a sub-cent floating-point residue that
 * keeps the "Refund" button visible.
 */
export function paymentRefundable(payment: Payment): number {
  return Math.max(
    0,
    Math.round((payment.amount - (payment.refunded_amount || 0)) * 100) / 100,
  );
}

/** One line item's own charge including its tax (dollars). Prefers the
 *  backend-authoritative tax-inclusive `calc__cost`; falls back to the
 *  pre-tax base plus `calc__taxes` on older/cached responses. Used to seed a
 *  per-line refund amount (FE-5). Excludes tips. */
export function lineItemChargeInclTax(
  item:
    | BookedService
    | Product
    | import("../types/transactions").TransactionServicePackage,
): number {
  const anyItem = item as {
    calc__cost?: number | null;
    calc__taxes?: number | null;
    calc__cost_excl_tips_discounts?: number | null;
    unit_price?: number;
    quantity?: number;
  };
  if (typeof anyItem.calc__cost === "number") {
    return Math.max(0, Math.round(anyItem.calc__cost * 100) / 100);
  }
  const preTax =
    typeof anyItem.calc__cost_excl_tips_discounts === "number"
      ? anyItem.calc__cost_excl_tips_discounts
      : (anyItem.unit_price ?? 0) * (anyItem.quantity ?? 1);
  const tax = anyItem.calc__taxes ?? 0;
  return Math.max(0, Math.round((preTax + tax) * 100) / 100);
}
